// src/systems/scoring.ts
//
// Phase 3 — pure scoring resolver. Imports only from ./bets and ../state/types.
// Order of effect application per fiche:
//   1. Resolve hit/miss (bets.ts)
//   2. Apply fiche effect (chips/mult adjustments)
//   3. Gambler short-circuit (return total=0 if not on a straight bet)
//   4. Apply flatMultBonus (relic-driven, e.g. lucky_charm/hot_streak/cold_streak)
//   5. Ball doubleAllPayouts (×2 on payout)
//   6. Boss halfPayouts (Math.floor(payout / 2))
//   7. Global payoutMultiplier (relic-driven, e.g. House Edge ×1.5)
//   8. total = Math.floor(chips × mult × payout)
//
// Phase 11 polish — CHAIN BONUS: after the per-fiche totals are computed,
// each scoring fiche (total > 0) is multiplied by 2^N where N is its 0-indexed
// position among scoring fiches in placement order. So the 1st scoring fiche
// gets ×1, 2nd ×2, 3rd ×4, 4th ×8 (Croupier's Smile case). Misses don't count
// toward the chain — they're skipped, not zeroed at index 0.

import type {
  Fiche,
  Ball,
  BoardLayout,
  Placement,
  SpinResult,
  SpinResult16,
  Slot,
  FicheEffect,
  BetType,
} from '../state/types';
import { resolveBet, basePayout } from './bets';

export interface ScoreInputs {
  placements: Placement[]; // up to 3 (or 4 with Croupier's Smile)
  fichesById: Record<string, Fiche>; // resolves placement.ficheId
  slot: Slot;
  ball: Ball | null;
  // Optional relic-driven adjustments (Phase 8 wires these)
  payoutMultiplier?: number; // global multiplier on payout (e.g. House Edge +50% → 1.5)
  bossHalfPayouts?: boolean; // boss modifier: payout halved (rounded down)
  bossRedHalf?: boolean; // red / outside-sub-red bets pay half
  bossBlackHalf?: boolean; // black / outside-sub-black bets pay half
  flatMultBonus?: number; // flat bonus added to mult before total (e.g. lucky_charm +5)
  /**
   * Phase 14 (SCORING-06): chess fiche bet expansion map.
   * Key = placement.ficheId (unique instance id).
   * Value = BetType[] from expandPlacement() in placements.ts.
   * Placements not in this map fall back to [placement.bet] (identity).
   * Chess fiches produce exactly ONE perFicheScores entry per placement (CHESS-04/05).
   */
  expandedBets?: Map<string, BetType[]>;
  /**
   * Phase 15 (BOARD-03): board layout for payoutMult read.
   * When provided, the per-placement payout is multiplied by the matching
   * cell's payoutMult (set by the merge consumable). Cells without payoutMult
   * default to ×1 (no change to vanilla payout).
   */
  boardLayout?: BoardLayout;
  /**
   * Phase 16 (SCORING-07): round-bounded last-spin snapshot for prev-spin fiche effects.
   * Undefined/null = first spin of round (no prior spin context).
   */
  lastSpin?: SpinResult16 | null;
  /**
   * Phase 16 (FICHE-15): count of 'standard' fiches in the player's deck.
   * Used by standardFicheCountBonus effect. Undefined = treat as 0.
   */
  standardFicheCount?: number;
  /**
   * 'one_for_all' relic: covering any subcell of a dozen covers the full
   * parent dozen (parentWhich 1/2/3 → all 12 numbers).  Dozen-sub bets in
   * the resolved expansion are promoted to `{ kind: 'dozen' }` bets, with
   * duplicates removed so the same dozen doesn't score multiple times.
   */
  oneForAll?: boolean;
  /** Additional slots to score at reduced payout (splash ball). */
  extraPayoutSlots?: { slot: Slot; scale: number }[];
}

export function scoreSpin(inputs: ScoreInputs): SpinResult {
  const {
    placements,
    fichesById,
    slot,
    ball,
    payoutMultiplier = 1,
    bossHalfPayouts = false,
    bossRedHalf = false,
    bossBlackHalf = false,
    flatMultBonus = 0,
    expandedBets,
    boardLayout,
  } = inputs;
  const lastSpin = inputs.lastSpin ?? null;
  const standardFicheCount = inputs.standardFicheCount ?? 0;
  const oneForAll = inputs.oneForAll ?? false;
  const extraPayoutSlots = inputs.extraPayoutSlots ?? [];
  const trayDefIds = placements.map((p) => fichesById[p.ficheId].defId);

  const perFicheScores = placements.map((placement) => {
    const fiche = fichesById[placement.ficheId];

    // Phase 14 (CHESS-04): determine the bets this placement covers.
    // Chess fiches supply a pre-computed expansion via expandedBets (keyed by ficheId,
    // which is the unique instance id like "chess_knight-1"). Non-chess fiches fall
    // back to [placement.bet] (identity — identical to pre-Phase-14 behaviour).
    let betsToResolve: BetType[] = expandedBets?.get(placement.ficheId) ?? [placement.bet];

    // 'one_for_all' relic: promote each dozen-sub to its full parent dozen,
    // deduplicated so hitting multiple subcells of the same dozen only scores once.
    if (oneForAll) {
      const dozensSeen = new Set<1 | 2 | 3>();
      const promoted: BetType[] = [];
      for (const b of betsToResolve) {
        if (b.kind === 'dozen-sub') {
          if (!dozensSeen.has(b.parentWhich)) {
            dozensSeen.add(b.parentWhich);
            promoted.push({ kind: 'dozen', which: b.parentWhich });
          }
        } else {
          promoted.push(b);
        }
      }
      betsToResolve = promoted;
    }

    // Resolve each bet against the spin slot and sum payouts of hits.
    // One perFicheScores entry is produced regardless of how many bets are in the expansion
    // (CHESS-05 invariant: perFicheScores.length === placements.length).
    let sumPayout = 0;
    let loopMultBonus = 0;
    for (const bet of betsToResolve) {
      const res = resolveBet(bet, slot, boardLayout);
      if (res.hit) {
        let p = res.payout;
        if (
          bossRedHalf &&
          (bet.kind === 'red' ||
            (bet.kind === 'outside-sub' && (bet as { parentKind: string }).parentKind === 'red'))
        ) {
          p = Math.floor(p / 2);
        }
        if (
          bossBlackHalf &&
          (bet.kind === 'black' ||
            (bet.kind === 'outside-sub' && (bet as { parentKind: string }).parentKind === 'black'))
        ) {
          p = Math.floor(p / 2);
        }
        sumPayout += p;
      }
      loopMultBonus += res.multBonus ?? 0;
    }

    // Splash ball: score adjacent slots at reduced rate.
    // Added to sumPayout BEFORE the miss check so fiches that only hit via
    // adjacent slots still score (main slot miss + splash hit = score).
    for (const { slot: extraSlot, scale } of extraPayoutSlots) {
      if (extraSlot.kind !== 'number') continue;
      for (const bet of betsToResolve) {
        const res = resolveBet(bet, extraSlot, boardLayout);
        if (res.hit) {
          let p = res.payout * scale;
          if (
            bossRedHalf &&
            (bet.kind === 'red' ||
              (bet.kind === 'outside-sub' && (bet as { parentKind: string }).parentKind === 'red'))
          ) {
            p = Math.floor(p / 2);
          }
          if (
            bossBlackHalf &&
            (bet.kind === 'black' ||
              (bet.kind === 'outside-sub' &&
                (bet as { parentKind: string }).parentKind === 'black'))
          ) {
            p = Math.floor(p / 2);
          }
          sumPayout += p;
        }
      }
    }

    if (sumPayout === 0) {
      // Miss: no bet hit, or expansion was empty (CHESS-06: void origin → []).
      return {
        ficheId: placement.ficheId,
        chips: fiche.chips,
        mult: fiche.mult,
        payout: 0,
        total: 0,
        hit: false,
      };
    }

    let chips = fiche.chips;
    let mult = fiche.mult;
    let payout = sumPayout;

    // Phase 15 (BOARD-03): apply payoutMult from the matching board cell.
    // merge consumable sets payoutMult on a cell; scoring multiplies payout by it.
    // Only applies when boardLayout is provided and the placement bet matches a cell
    // with payoutMult > 1. Uses betsMatchForScoring (same-kind structural match).
    //
    // WR-05 invariant: betsMatchForScoring uses Array.find (first match). This is
    // correct as long as no two cells share a structurally identical bet. swapCells
    // can transiently create duplicates (e.g. straight:5 ↔ straight:5), which would
    // cause the first cell's payoutMult to apply to both placements. This is
    // accepted under v1 constraints. A correct fix would store boardCellIdx on
    // Placement and look up by index — deferred to a future phase.
    //
    // Also capture chipsBonus/multBonus for application after fiche effects below.
    let cellChipsBonus = 0;
    let cellMultBonus = 0;
    if (boardLayout) {
      const matchingCell = boardLayout.cells.find((c) => betsMatchForScoring(c.bet, placement.bet));
      if (matchingCell?.payoutMult && matchingCell.payoutMult > 1) {
        payout = payout * matchingCell.payoutMult;
      }
      if (matchingCell?.chipsBonus) cellChipsBonus = matchingCell.chipsBonus;
      if (matchingCell?.multBonus) cellMultBonus = matchingCell.multBonus;
    }

    // Apply fiche effect (chessMove has no chips/mult change — it breaks immediately).
    // For other effect types: behaviour unchanged from pre-Phase-14.
    // NOTE: applyFicheEffect receives placement.bet (the origin cell's bet), not the
    // expansion sum. This is intentional: plusChipsIfPayoutAtLeast uses basePayout
    // of the origin bet, not the expansion total.
    // Phase 16: lastSpin and standardFicheCount are threaded in for prev-spin effects.
    if (fiche.effect) {
      const result = applyFicheEffect(
        fiche,
        fiche.effect,
        placement,
        slot,
        trayDefIds,
        lastSpin,
        standardFicheCount,
      );
      chips = result.chips;
      mult = result.mult;
    }

    // Apply cell chipsBonus/multBonus after fiche effects (so they stack on top of
    // the fiche's own chips/mult, not replacing them).
    chips += cellChipsBonus;
    mult += cellMultBonus;

    // Gambler: only scores on straight bets (origin bet kind, not expansion).
    if (fiche.effect?.type === 'doubleIfStraight' && placement.bet.kind !== 'straight') {
      return {
        ficheId: placement.ficheId,
        chips: fiche.chips,
        mult: fiche.mult,
        payout,
        total: 0,
        hit: false,
      };
    }

    // Apply relic-driven flat mult bonus (lucky_charm, hot_streak, cold_streak).
    // Added AFTER fiche.effect/Gambler short-circuit but BEFORE payout cascade so
    // it stacks linearly with fiche.mult before the chips × mult × payout multiply.
    mult = mult + flatMultBonus + loopMultBonus;

    // Apply ball payout effects
    if (ball?.effect?.type === 'doubleAllPayouts') {
      payout *= 2;
    }

    // Apply global payout multiplier (e.g. House Edge relic +50%)
    payout = payout * payoutMultiplier;

    const total = Math.floor(chips * mult * payout);

    return { ficheId: placement.ficheId, chips, mult, payout, total, hit: true };
  });

  // Chain bonus: each scoring fiche's total ×2^(scoring-index) in placement order.
  // Misses (total === 0) don't advance the chain. 1st score ×1, 2nd ×2, 3rd ×4, 4th ×8.
  let scoringIdx = 0;
  const chainedScores = perFicheScores.map((s) => {
    if (s.total <= 0) return s;
    const factor = Math.pow(2, scoringIdx);
    scoringIdx++;
    return { ...s, total: s.total * factor };
  });

  const rawScore = chainedScores.reduce((sum, s) => sum + s.total, 0);
  const spinScore = bossHalfPayouts ? Math.floor(rawScore / 2) : rawScore;

  return { slot, placements, perFicheScores: chainedScores, spinScore };
}

function applyFicheEffect(
  fiche: Fiche,
  effect: FicheEffect,
  placement: Placement,
  slot: Slot,
  trayDefIds: string[],
  lastSpin: SpinResult16 | null,
  standardFicheCount: number,
): { chips: number; mult: number } {
  let chips = fiche.chips;
  let mult = fiche.mult;

  switch (effect.type) {
    case 'plusMultIfNumber':
      if (slot.kind === 'number' && slot.value === effect.number) mult += effect.bonus;
      break;
    case 'plusMultIfColor':
      if (slot.kind === 'number' && slot.color === effect.color) mult += effect.bonus;
      break;
    case 'plusChipsIfPayoutAtLeast': {
      const baseP = basePayout(placement.bet);
      if (baseP >= effect.threshold) chips += effect.bonus;
      break;
    }
    case 'doubleIfStraight':
      if (placement.bet.kind === 'straight') {
        chips *= 2;
        mult *= 2;
      }
      break;
    case 'plusMultPerSameDefInTray': {
      const sameCount = trayDefIds.filter((d) => d === fiche.defId).length - 1; // exclude self
      mult += effect.bonus * sameCount;
      break;
    }
    case 'plusMultPerCoveredEven': {
      const evenCovered = countEvenCoveredByBet(placement);
      mult += effect.bonus * evenCovered;
      break;
    }
    case 'plusChipsIfColor': {
      // House Special: dual-condition. The catalogue entry encodes the LOSS leg
      // (red, sign='-', percent=50). For defId === 'house_special' we also apply
      // the inverse leg (+50% chips when slot.color === 'black').
      // Phase 13: narrow on slot.kind before accessing slot.color.
      if (slot.kind === 'number' && slot.color === effect.color) {
        chips = Math.floor(chips * (1 + (effect.sign === '+' ? 1 : -1) * (effect.percent / 100)));
      } else if (slot.kind === 'number' && fiche.defId === 'house_special') {
        const otherColor = effect.color === 'red' ? 'black' : 'red';
        if (slot.color === otherColor) {
          const oppositeSign = effect.sign === '+' ? -1 : +1;
          chips = Math.floor(chips * (1 + oppositeSign * (effect.percent / 100)));
        }
      }
      break;
    }
    case 'chessMove':
      // Expansion-only effect: no chips/mult modification.
      // Bet expansion is handled upstream by expandedBets in ScoreInputs.
      break;
    // Phase 16 (FICHE-12): prev-spin-state fiche effects
    case 'plusMultIfPrevSpinHit':
      if (lastSpin && lastSpin.hitCount > 0) {
        mult += effect.bonus;
      }
      break;
    case 'plusChipsPerStreak':
      chips += effect.chipsPerStreak * (lastSpin?.streak ?? 0);
      break;
    case 'bonusIfPrevSpinZero':
      if (lastSpin?.wasZero) {
        chips += effect.bonusChips;
      }
      break;
    // Phase 16 (FICHE-15): standard-fiche-count synergy
    case 'standardFicheCountBonus':
      chips += effect.chipsPerStandard * standardFicheCount;
      break;
    case 'cellSwap':
      // Phase 19 (FICHE-19): Side-effect-only fiche; the actual board swap
      // is performed in RoundScene post-score dispatch (plan 19-02 task 3).
      // Scoring returns chips/mult unchanged — the cell still scores normally
      // on the spin where it is placed; the swap takes effect AFTERWARD for
      // subsequent spins.
      break;
    case 'tetrisShape':
      // Phase 23 (FICHE-23): Expansion-only effect — coverage is handled
      // upstream by expandPlacement() / expandedBets in ScoreInputs. No
      // chips/mult modification here (analogous to chessMove).
      break;
  }

  return { chips, mult };
}

/**
 * Phase 15 (BOARD-03): structural bet equality used by payoutMult lookup.
 * Mirrors betsMatchForPreview in Table.ts / RoundScene.ts — duplicated here
 * to avoid cross-layer imports (scoring.ts must not import from UI layer).
 */
function betsMatchForScoring(a: BetType, b: BetType): boolean {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case 'straight':
      return a.number === (b as typeof a).number;
    case 'dozen':
    case 'column':
      return a.which === (b as typeof a).which;
    case 'split': {
      const sa = [...a.numbers].sort((x, y) => x - y);
      const sb = [...(b as typeof a).numbers].sort((x, y) => x - y);
      return sa[0] === sb[0] && sa[1] === sb[1];
    }
    case 'corner':
      return (
        [...a.numbers].sort((x, y) => x - y).join(',') ===
        [...(b as typeof a).numbers].sort((x, y) => x - y).join(',')
      );
    case 'outside-sub': {
      const bb = b as typeof a;
      if (a.parentKind !== bb.parentKind) return false;
      if (a.numbers.length !== bb.numbers.length) return false;
      const sa = [...a.numbers].sort((x, y) => x - y);
      const sb = [...bb.numbers].sort((x, y) => x - y);
      return sa.every((v, i) => v === sb[i]);
    }
    default:
      // red, black, even, odd, low, high — kind match is sufficient
      return true;
  }
}

export function countEvenCoveredByBet(placement: Placement): number {
  switch (placement.bet.kind) {
    case 'straight':
      return placement.bet.number !== 0 && placement.bet.number % 2 === 0 ? 1 : 0;
    case 'red':
      return 8; // {12,14,16,18,30,32,34,36}
    case 'black':
      return 10; // {2,4,6,8,10,20,22,24,26,28}
    case 'even':
      return 18;
    case 'odd':
      return 0;
    case 'low':
      return 9; // [1,18] evens = {2,4,6,8,10,12,14,16,18}
    case 'high':
      return 9; // [19,36] evens = {20,22,24,26,28,30,32,34,36}
    case 'dozen':
      return 6; // any dozen has 6 evens
    case 'column':
      // Col 1: evens {4,10,16,22,28,34} = 6
      // Col 2: evens {2,8,14,20,26,32} = 6
      // Col 3: evens {6,12,18,24,30,36} = 6
      return 6;
    case 'split':
      return placement.bet.numbers.filter((n) => n !== 0 && n % 2 === 0).length;
    case 'corner':
      return placement.bet.numbers.filter((n) => n !== 0 && n % 2 === 0).length;
    case 'outside-sub':
      return placement.bet.numbers.filter((n) => n !== 0 && n % 2 === 0).length;
    case 'dozen-sub':
      return placement.bet.numbers.filter((n) => n !== 0 && n % 2 === 0).length;
  }
}
